import React, { useState, useEffect, useRef } from 'react';
import { useBrandData } from './useBrandData';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import { Brain, Sparkles, RefreshCcw } from 'lucide-react';
import { toast } from '@/components/ui/use-toast';
import { Database } from '@/database.types'
import { FieldMetadataMap } from '../../../workers/src/_shared/fieldMetadata.types';
import { getFieldMetadata } from '@/lib/services/fieldMetadata';
import skeletonDemo from '@/components/ui/skeleton.demo';
import confetti from 'canvas-confetti';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';

type Brand = Database['public']['Tables']['brands']['Row']

interface AIGenerateDialogProps {
    fieldName: keyof Brand;
    defaultInstructions: string;
    fieldMetadata: {
        description?: string;
        bestPractices?: string;
    };
    brand: Brand;
    existingValue?: string | undefined;
    initialFeedback?: string;
    onApply: (field: keyof Brand, content: string) => void;
    onClose: () => void;
}

const AIGenerateDialog: React.FC<AIGenerateDialogProps> = ({
    fieldName,
    brand,
    defaultInstructions,
    fieldMetadata: propFieldMetadata,
    existingValue,
    initialFeedback = '',
    onApply,
    onClose,
}) => {
    const { generateAIContent } = useBrandData();
    const [showDialog, setShowDialog] = useState(true);
    const [generatedContent, setGeneratedContent] = useState(existingValue || '');
    const [isGenerating, setIsGenerating] = useState(false);
    const [fieldMetadata, setFieldMetadata] = useState<FieldMetadataMap | null>(null);
    const initialGenerationRef = useRef(false);
    const [feedback, setFeedback] = useState(initialFeedback);
    const [previousGeneration, setPreviousGeneration] = useState<string | null>(null);

    useEffect(() => {
        getFieldMetadata().then(setFieldMetadata).catch(console.error);
    }, []);

    const field = fieldMetadata?.[fieldName];

    const handleGenerate = async () => {
        if (!brand?.id) {
            console.error('Brand ID is required for content generation');
            return;
        }

        setIsGenerating(true);
        try {
            // Save the current content if we're regenerating
            if (generatedContent) {
                setPreviousGeneration(generatedContent);
            }

            const additionalInstructions = defaultInstructions.trim() || field?.bestPractices || '';
            
            // Include feedback and previous generation if they exist
            const feedbackInfo = feedback ? 
                `User feedback: ${feedback}. Previous generation: "${generatedContent}". ` : 
                '';
            
            const result = await generateAIContent(
                brand.id, 
                fieldName, 
                `${feedbackInfo}${additionalInstructions}`,
                existingValue,
                feedback
            );
            
            setGeneratedContent(result);
            
            // Clear feedback after successful generation
            if (feedback) {
                setFeedback('');
            }
        } catch (error) {
            console.error('Error generating content:', error);
            
            // Check if this is a provider outage
            const errorMessage = error instanceof Error ? error.message : String(error);
            const isProviderOutage = 
                errorMessage.includes('service unavailable') || 
                errorMessage.includes('provider error') ||
                errorMessage.includes('insufficient_quota') ||
                errorMessage.includes('exceeded your current quota') ||
                (error as any)?.isProviderOutage;
            
            toast({
                title: isProviderOutage ? 'Service Unavailable' : 'Error',
                description: isProviderOutage 
                    ? 'AI service is temporarily unavailable. Please try again later.'
                    : 'Failed to generate content. Please try again.',
                variant: 'destructive',
            });
            
            // If it's a provider outage, show a helpful message in the textarea
            if (isProviderOutage) {
                setGeneratedContent('The AI service is temporarily unavailable. This is likely due to high demand or a temporary outage. Please try again in a few minutes.');
            }
        } finally {
            setIsGenerating(false);
        }
    };

    // Generate content once when dialog opens
    useEffect(() => {
        // If we have an existing value and feedback, don't auto-generate
        // Otherwise, generate content when the dialog opens for the first time
        if (!initialGenerationRef.current && brand?.id && !isGenerating && !existingValue) {
            initialGenerationRef.current = true;
            handleGenerate();
        }
    }, [brand?.id, existingValue]);

    const handleCancel = () => {
        setShowDialog(false);
        onClose();
    };

    const handleApply = () => {
        confetti({
            particleCount: 100,
            spread: 70,
            origin: { y: 0.6 }
        });
        onApply(fieldName, generatedContent);
        setShowDialog(false);
        onClose();
    };

    return (
        <Dialog open={showDialog} onOpenChange={(open) => {
            if (!open) handleCancel();
        }}>
            <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto bg-white">
                <DialogHeader className="space-y-2">
                    <div className="flex items-center gap-2">
                        <Brain className="h-6 w-6 text-primary animate-pulse" />
                        <DialogTitle className="text-xl">Brand Genius</DialogTitle>
                    </div>
                    <DialogDescription className="text-base">
                        Generating {fieldName.split('_').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
                    </DialogDescription>
                </DialogHeader>

                {propFieldMetadata.description && (
                    <Alert className="bg-muted/50 border-none mt-2">
                        <AlertDescription className="text-sm text-muted-foreground">
                            {propFieldMetadata.description}
                        </AlertDescription>
                    </Alert>
                )}

                <div className="space-y-4 mt-4">
                    {isGenerating ? (
                        <div className="flex items-center justify-center min-h-[200px]">
                            <skeletonDemo.SkeletonCard />
                        </div>
                    ) : (
                        <>
                            <Textarea
                                value={generatedContent}
                                onChange={(e) => setGeneratedContent(e.target.value)}
                                rows={8}
                                className="min-h-[200px] max-h-[40vh] overflow-y-auto"
                            />

                            {previousGeneration && (
                                <div className="text-xs text-muted-foreground">
                                    <Button 
                                        variant="link" 
                                        size="sm" 
                                        className="p-0 h-auto"
                                        onClick={() => setGeneratedContent(previousGeneration)}
                                    >
                                        Revert to previous generation
                                    </Button>
                                </div>
                            )}

                            <div className="space-y-2">
                                <Label htmlFor="feedback">Feedback for regeneration</Label>
                                <Textarea
                                    id="feedback"
                                    value={feedback}
                                    onChange={(e) => setFeedback(e.target.value)}
                                    placeholder="Provide feedback to improve the generated content..."
                                    rows={2}
                                />
                            </div>

                            <Button 
                                onClick={handleGenerate} 
                                disabled={isGenerating}
                                variant={feedback ? "default" : "outline"}
                                className="w-full"
                            >
                                {feedback ? (
                                    <>
                                        <RefreshCcw className="mr-2 h-4 w-4" />
                                        Regenerate with Feedback
                                    </>
                                ) : (
                                    <>
                                        <Sparkles className="mr-2 h-4 w-4" />
                                        Generate
                                    </>
                                )}
                            </Button>
                        </>
                    )}
                    <div className="flex justify-end space-x-2">
                        <Button onClick={handleCancel} variant="outline">
                            Cancel
                        </Button>
                        <Button onClick={handleApply} disabled={!generatedContent}>
                            Use
                        </Button>
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
}

export default AIGenerateDialog;
